/* @flow */

import * as React from 'react';
import {useSelector} from 'react-redux';

import {Markets} from 'nutshell-core/markets';
import {orderProductsByMarketAvailability} from 'nutshell-core/order-products-by-market-availability';
import {Products} from 'nutshell-core/products';
import {Session} from 'nutshell-core/session';
import type {Product, Market, ProductId} from 'nutshell-core/types';

import {OptionWithSubtitleAndSublabel} from 'shells/select-option-components/option-with-subtitle-and-sublabel';
import type {Option, SelectSize, FooterButtonProps} from 'shells/select-picker-generic';

import {appHistory} from '../../../history';

import {GraphQLSelect} from './graphql-select';

type Props = {|
    onChange: (option: Option) => void,
    selectedId: ?string,
    size?: SelectSize,
    hideDivider?: boolean,
    defaultMenuIsOpen?: boolean,
    placeholder?: string,
    currentMarket?: ?Market,
    excludeProductIds?: ProductId[],
    onBlur?: () => void,
    onClear?: () => void,
    getRef?: (ref: any) => void,
    footerButton?: FooterButtonProps,
    maxMenuHeight?: number,
    noOptionsMessage?: () => string,
    menuZIndex?: number,
|};

export function ReduxSelectProducts(props: Props) {
    const allProducts = useSelector((state) => Products.getAll(state));
    const productsState = useSelector((state) => state.products);
    const defaultMarket = useSelector((state) => Markets.getDefaultMarket(state));
    const loggedInUser = useSelector((state) => Session.getSessionStateUser(state));
    const isLoading = productsState ? productsState.isLoading : false;

    const currentMarket = props.currentMarket || defaultMarket;

    // Filter products based on market
    let filteredProducts = allProducts;
    if (currentMarket && currentMarket.id) {
        filteredProducts = allProducts.filter(
            (product) => product.prices && product.prices[currentMarket.id]
        );
    }

    // Always include the currently selected product, even if filtered out
    // This prevents the selection from disappearing when the product doesn't match filters
    if (props.selectedId) {
        const selectedProduct = allProducts.find((p) => p.id === props.selectedId);
        if (selectedProduct && !filteredProducts.find((p) => p.id === props.selectedId)) {
            filteredProducts = [...filteredProducts, selectedProduct];
        }
    }

    // Filter out excluded products (but not the currently selected one)
    if (props.excludeProductIds && props.excludeProductIds.length > 0) {
        const excludeIds = props.excludeProductIds;
        filteredProducts = filteredProducts.filter(
            (product) => product.id === props.selectedId || !excludeIds.includes(product.id)
        );
    }

    // Order products by market availability if we have a market
    let orderedProducts;
    if (currentMarket && currentMarket.id) {
        orderedProducts = orderProductsByMarketAvailability(filteredProducts, currentMarket.id);
    } else {
        orderedProducts = filteredProducts;
    }

    const productOptions = orderedProducts
        ? orderedProducts.map((product: Product) => ({
              label: product.name,
              value: product.id,
              subtitle: product.sku,
              ...(NutClientConfig.hasProductCategoriesFF
                  ? {
                        sublabel:
                            product.category && product.category.name
                                ? product.category.name
                                : 'No category',
                    }
                  : {}),
          }))
        : [];

    const defaultFooterButton = loggedInUser &&
        loggedInUser.permissions.canAccessSetup && {
            label: 'Manage products',
            onClick: () => {
                appHistory.push(
                    NutClientConfig.hasInvoicesFF ? '/sales/product' : '/setup/product'
                );
            },
        };
    const defaultNoOptionsMessage = () => {
        if (allProducts.length && !filteredProducts.length && currentMarket) {
            return 'No products in current market';
        }

        return 'No products found';
    };

    return (
        <GraphQLSelect
            isLoading={isLoading}
            defaultMenuIsOpen={
                props.defaultMenuIsOpen !== undefined ? props.defaultMenuIsOpen : true
            }
            placeholder={
                props.placeholder ||
                (NutClientConfig.hasProductCategoriesFF
                    ? 'Search by product, sku, or category…'
                    : 'Search by product or sku…')
            }
            models={productOptions}
            labelKey='label'
            valueKey='value'
            filterOption={(option, search) => {
                if (option.data) {
                    return (
                        (option.data.subtitle &&
                            option.data.subtitle.toLowerCase().includes(search.toLowerCase())) ||
                        (option.data.label &&
                            option.data.label.toLowerCase().includes(search.toLowerCase())) ||
                        (option.data.sublabel &&
                            option.data.sublabel.toLowerCase().includes(search.toLowerCase()))
                    );
                }

                return option.label.toLowerCase().includes(search.toLowerCase());
            }}
            components={{
                Option: OptionWithSubtitleAndSublabel,
            }}
            onChange={props.onChange}
            selectedId={props.selectedId}
            size={props.size}
            hideDivider={props.hideDivider}
            onBlur={props.onBlur}
            onClearValue={props.onClear}
            getRef={props.getRef}
            footerButton={props.footerButton || defaultFooterButton}
            maxMenuHeight={props.maxMenuHeight}
            noOptionsMessage={props.noOptionsMessage || defaultNoOptionsMessage}
            menuZIndex={props.menuZIndex}
        />
    );
}
